import React, { useState, useEffect } from 'react';
import {
    TextField,
    FormControl,
    InputLabel,
    Box,
    Autocomplete,
    Popover,
    OutlinedInput,
    Button,
    Badge,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { debounce } from 'lodash';
import YearPicker from './YearPicker';
import { getPolygonSuggestions } from '../../services/apiClient';
import { useAppSelector } from '../../store/hooks';
import { MonthSelector } from './MonthSelector';
import { TagsMultiSelect } from './TagsMultiSelect';
import FilterListIcon from '@mui/icons-material/FilterList';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const ControlsContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
}));

interface SearchFilterControlsProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  startYear: number;
  onStartYearChange: (year: number) => void;
  endYear: number;
  onEndYearChange: (year: number) => void;
  months: number[];
  onMonthsChange: (months: number[]) => void;
  availableTags: string[];
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  availableYears?: number[];
  onHistoryFilterClick: () => void;
  historyFilters?: Record<string, string[]>;
  onResetAll?: () => void;
}

const monthsNamelist = [
    'Jan','Feb','Mar','Apr','May','Jun',
    'Jul','Aug','Sep','Oct','Nov','Dec'
];

const SearchFilterControls: React.FC<SearchFilterControlsProps> = ({
  searchQuery,
  onSearchChange,
  startYear,
  onStartYearChange,
  endYear,
  onEndYearChange,
  months,
  onMonthsChange,
  availableTags,
  selectedTags,
  onTagsChange,
  availableYears,
  onHistoryFilterClick,
  historyFilters = {},
  onResetAll,
}) => {
    const [inputValue, setInputValue] = useState(searchQuery);
    const [suggestions, setSuggestions] = useState<{ label: string; value: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const { selectedAccountId } = useAppSelector((state) => state.app);
    const [monthAnchorEl, setMonthAnchorEl] = React.useState<HTMLElement | null>(null);

    const handleMonthsClick = (event: React.MouseEvent<HTMLElement>) => {
        setMonthAnchorEl(event.currentTarget);
    };

    const handleMonthsClose = () => {
        setMonthAnchorEl(null);
    };

    const handleMonthsApply = (newMonths: number[]) => {
        onMonthsChange(newMonths);
        handleMonthsClose();
    }

    const formatMonths = (range: number[]) => {
        if (range.length === 0) return "All";
        const start = monthsNamelist[range[0] - 1];
        const end = monthsNamelist[range[range.length - 1] - 1];
        if (start === end) return start;
        return `${start} - ${end}`;
    }

    const openMonthPopover = Boolean(monthAnchorEl);
    const monthPopoverId = openMonthPopover ? 'month-range-popover' : undefined;

    // Calculate total count of selected history filter values
    const historyFilterCount = Object.values(historyFilters).reduce(
      (total, values) => total + (values?.length || 0),
      0
    );

    const handleSearchDebounced = debounce(async (query: string) => {
        if (query.length < 3) {
            setSuggestions([]);
            return;
        }
        setLoading(true);
        try {
            const results = await getPolygonSuggestions(query, selectedAccountId);
            const formattedSuggestions = results.map((item) => ({
                label: `${item.name}, ${item.country}`,
                value: item.name,
            }));
            setSuggestions(formattedSuggestions);
        } catch (error) {
            console.error('Failed to fetch suggestions:', error);
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    }, 300);

    useEffect(() => {
        setInputValue(searchQuery);
    }, [searchQuery]);

    useEffect(() => {
        return () => {
            handleSearchDebounced.cancel();
        };
    }, [handleSearchDebounced]);

  return (
    <ControlsContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Autocomplete
            freeSolo
            options={suggestions}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
            onInputChange={(_event, newInputValue) => {
                setInputValue(newInputValue);
                handleSearchDebounced(newInputValue);
            }}
            onChange={(_event, newValue) => {
                const finalValue = typeof newValue === 'string' ? newValue : newValue?.label || '';
                onSearchChange(finalValue);
            }}
            inputValue={inputValue}
            loading={loading}
            sx={{ width: 340 }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Search polygons by name"
                    variant="outlined"
                />
            )}
        />
        <TagsMultiSelect
            availableTags={availableTags}
            selectedTags={selectedTags}
            onTagsChange={onTagsChange}
        />
        <YearPicker
            label="Start Year"
            value={new Date(startYear, 0, 1)}
            onChange={(date) => date && onStartYearChange(date.getFullYear())}
            minDate={new Date(2000, 0, 1)}
            maxDate={new Date(endYear, 11, 31)}
            availableYears={availableYears}
        />
        <YearPicker
            label="End Year"
            value={new Date(endYear, 0, 1)}
            onChange={(date) => date && onEndYearChange(date.getFullYear())}
            minDate={new Date(startYear, 0, 1)}
            maxDate={new Date()}
            availableYears={availableYears}
        />
      <FormControl sx={{ width: 120 }}>
        <InputLabel>Selected Months</InputLabel>
        <OutlinedInput
            readOnly
            label="Selected Months"
            value={formatMonths(months)}
            onClick={handleMonthsClick}
            sx={{
                '& .MuiInputBase-input': {
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                },
            }}
        />
      </FormControl>
      <Popover
        id={monthPopoverId}
        open={openMonthPopover}
        anchorEl={monthAnchorEl}
        onClose={handleMonthsClose}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
        }}
      >
        <MonthSelector
            initialMonths={months}
            onApply={handleMonthsApply}
            onClose={handleMonthsClose}
        />
      </Popover>
      <Badge
        badgeContent={historyFilterCount}
        color="error"
        sx={{
          '& .MuiBadge-badge': {
            top: 4,
            right: 4,
          },
        }}
      >
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={onHistoryFilterClick}
          sx={{
            height: '56px', // Match MUI OutlinedInput default height
            minHeight: '56px',
          }}
        >
          Additional Filters
        </Button>
      </Badge>
      {onResetAll && (
        <Button
          variant="outlined"
          startIcon={<RestartAltIcon />}
          onClick={onResetAll}
          sx={{
            height: '56px', // Match MUI OutlinedInput default height
            minHeight: '56px',
          }}
        >
          Reset All
        </Button>
      )}
      </Box>
    </ControlsContainer>
  );
};

export default SearchFilterControls;
